import React, { Component } from 'react';
import NotificationPanel from './NotificationPanel/index';
import Events from '@EventEmitter';
import { inject, observer } from 'mobx-react';
import { withStyles } from '@material-ui/core';
import { compose } from 'recompose';
import withWidth from '@material-ui/core/withWidth';
import { RMAXLogger } from '@RMAXLogger';
import { NOTIFICATIONS_CONTENT } from './content';
import {
  TOAST_NOTIFICATION_EVENTS,
  TOAST_NOTIFICATION_LIMITS,
  NOTIFICATION_TYPES,
} from './constants';
import type { DriverMessageItem, Item, NotificationHandlers } from './types';

const styles = () => ({
  notificationContainer: {
    height: 'auto',
  },
  desktopContainer: {
    top: 43,
  },
  tabContainer: {
    top: 45,
    right: -28,
  },
});

type ToastNotificationsProps = {
  classes: Record<string, string>;
  width?: string;
  openDriverChatBox: NotificationHandlers['openDriverChatBox'];
  onPickupReviewClick?: NotificationHandlers['onPickupReviewClick'];
  DispatchStore?: {
    getCurrentDriver: (sender: string) => DriverMessageItem['driver'];
  };
};

type ToastNotificationsState = {
  toastsNotifications: Item[];
};

@inject('DispatchStore')
@observer
export class ToastNotifications extends Component<
  ToastNotificationsProps,
  ToastNotificationsState
> {
  timers: Set<ReturnType<typeof setTimeout>>;

  constructor(props: ToastNotificationsProps) {
    super(props);

    this.state = {
      toastsNotifications: [],
    };
    this.timers = new Set();
  }

  componentDidMount() {
    Events.on(TOAST_NOTIFICATION_EVENTS.showToastMessageNotification, this.showToastNotification);
    Events.on(
      TOAST_NOTIFICATION_EVENTS.showToastPickupReviewNotification,
      this.showPickupReviewToast
    );
  }

  componentWillUnmount() {
    Events.off(TOAST_NOTIFICATION_EVENTS.showToastMessageNotification, this.showToastNotification);
    Events.off(
      TOAST_NOTIFICATION_EVENTS.showToastPickupReviewNotification,
      this.showPickupReviewToast
    );
    this.clearAllTimers();
  }

  clearAllTimers = () => {
    this.timers.forEach((timer) => clearTimeout(timer));
    this.timers.clear();
  };

  enqueueToast = (toastItem: Item) => {
    this.setState((prevState) => ({
      toastsNotifications: [toastItem, ...prevState.toastsNotifications].slice(
        0,
        TOAST_NOTIFICATION_LIMITS.maxVisible
      ),
    }));

    const timer = setTimeout(() => {
      this.timers.delete(timer);
      this.removeToastById(toastItem.id);
    }, TOAST_NOTIFICATION_LIMITS.dismissMs);
    this.timers.add(timer);
  };

  showToastNotification = (data: string) => {
    let msgData: DriverMessageItem;

    try {
      msgData = JSON.parse(data);
    } catch (error) {
      RMAXLogger.error(NOTIFICATIONS_CONTENT.logging.toastParseFailed, {
        message: error?.message
      });
      return;
    }

    msgData.driver = this.props.DispatchStore?.getCurrentDriver(msgData.sender);
    if (!msgData.driver) {
      return;
    }

    msgData.type = NOTIFICATION_TYPES.DRIVER_MESSAGE;
    msgData.timestamp = msgData.messageDateTs;
    msgData.id = msgData.id || `driver-${msgData.messageId || msgData.sender}-${msgData.messageDateTs}`;
    this.enqueueToast(msgData);
  };

  showPickupReviewToast = (item: Item) => {
    if (!item?.type) {
      return;
    }
    this.enqueueToast(item);
  };

  removeToastById = (toastId: string) => {
    this.setState((prevState) => ({
      toastsNotifications: prevState.toastsNotifications.filter((toast) => toast.id !== toastId),
    }));
  };

  render() {
    const { toastsNotifications } = this.state;
    const { classes, width, openDriverChatBox, onPickupReviewClick } = this.props;
    return (
      <>
        {Boolean(toastsNotifications.length) && (
          <NotificationPanel
            data={toastsNotifications}
            showFilterChips={false}
            handlers={{
              openDriverChatBox,
              onPickupReviewClick: onPickupReviewClick || (() => {}),
            }}
            containerStyles={`${
              ['xl', 'lg'].includes(width || '')
                ? classes.desktopContainer
                : classes.tabContainer
            } ${classes.notificationContainer}`}
          />
        )}
      </>
    );
  }
}

export default compose(withWidth(), withStyles(styles))(ToastNotifications);
